import { getNbaDisplayName } from "../../adapters/nbaPlayerAdapter";
import { DESTINATION_LABELS, SPORT_BY_ID } from "../../constants/sportWorkspace";
import { getPlayerPath } from "../../utils/fanPage";
import { getSportPath, isDestinationLive } from "../../utils/sportWorkspace";
import { formatPlayerName } from "../../utils/textUtils";
import { resolveMlbTeamId } from "../../utils/teamLogoUrl";

export const DIRECTORY_GROUPS = {
  players: "Players",
  teams: "Teams",
};

const SHORTCUT_IDS = ["allTimeGreats", "compare", "teams"];
export const MIN_DIRECTORY_QUERY_LENGTH = 2;

export function pickBySport(sport, variants = {}, fallback = null) {
  if (Object.prototype.hasOwnProperty.call(variants, sport)) {
    return variants[sport];
  }
  return fallback;
}

const MLB_CATALOG = {
  sport: "mlb",
  title: "MLB Players",
  overline: "Baseball",
  subtitle: "Search by player or team, or start from a franchise.",
  placeholder: "Search players or teams...",
  teamsEnabled: true,
  featured: {
    title: "All-time HR leaders",
    subtitle: "Tap a legend to open their page",
    statLabel: "Career HR",
  },
  teamsRail: {
    title: "Browse by team",
    subtitle: "Open a franchise to find its players",
  },
};

const NBA_CATALOG = {
  sport: "nba",
  title: "NBA Players",
  overline: "Basketball",
  subtitle: "Search by player or team, or start from a franchise.",
  placeholder: "Search players or teams...",
  teamsEnabled: true,
  featured: {
    title: "All-time scoring leaders",
    subtitle: "Tap a legend to open their page",
    statLabel: "PTS",
  },
  teamsRail: {
    title: "Browse by team",
    subtitle: "Open a franchise to find its players",
  },
};

const NFL_CATALOG = {
  sport: "nfl",
  title: "NFL Players",
  overline: "Football",
  subtitle: "Search by player or team, or start from a franchise.",
  placeholder: "Search players or teams...",
  teamsEnabled: true,
  featured: {
    title: "All-time passing yards",
    subtitle: "Tap a legend to open their page",
    statLabel: "PASS YDS",
  },
  teamsRail: {
    title: "Browse by team",
    subtitle: "Open a franchise to find its players",
  },
};

const CATALOGS = {
  mlb: MLB_CATALOG,
  nba: NBA_CATALOG,
  nfl: NFL_CATALOG,
};

export function getPlayerSearchCatalog(sport) {
  const base = CATALOGS[sport] || {
    ...NFL_CATALOG,
    sport,
    title: `${SPORT_BY_ID[sport]?.label || "Players"}`,
  };
  return {
    ...base,
    shortcuts: SHORTCUT_IDS.filter((id) => isDestinationLive(base.sport, id)).map(
      (id) => ({
        id,
        label: DESTINATION_LABELS[id],
        path: getSportPath(base.sport, id),
      })
    ),
  };
}

export function getTeamDetailPath(sport, teamName) {
  const name = String(teamName || "").trim();
  if (!name) return null;
  return `${getSportPath(sport, "teams")}/${encodeURIComponent(name)}`;
}

export function pathForDirectoryOption(option, sport) {
  if (!option?.id) return null;
  if (option.type === "team") return getTeamDetailPath(sport, option.id);
  return getPlayerPath(option.id, sport);
}

export function filterTeamNames(names = [], query = "") {
  const needle = String(query).trim().toLowerCase();
  if (needle.length < MIN_DIRECTORY_QUERY_LENGTH) return [];
  return names.filter((name) => String(name).toLowerCase().includes(needle));
}

export function railTeamsFromNames(names = []) {
  const byId = new Map();
  names.forEach((name) => {
    const mlbId = resolveMlbTeamId(name);
    if (!mlbId) return;
    const current = byId.get(mlbId);
    if (!current || String(name).length < current.name.length) {
      byId.set(mlbId, { name: String(name), mlbId });
    }
  });
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function playerLabel(player, sport) {
  return sport === "nba" ? getNbaDisplayName(player) : formatPlayerName(player);
}

function playerSecondary(player, sport, catalog) {
  if (sport === "nba") {
    const points =
      player.popularityScore ?? player.stats?.totals?.career?.pts ?? player.points;
    const position = player.bio?.position || player.bioData?.position;
    if (position && points != null) return `${position} · PTS: ${points}`;
    if (points != null) return `PTS: ${points}`;
    return position || "";
  }
  if (sport === "nfl") {
    const yards =
      player.popularityScore ?? player.stats?.passing?.career?.yds ?? player.passingYards;
    const position = player.bio?.position || player.bioData?.position;
    if (position && yards != null) return `${position} · Pass Yds: ${yards}`;
    if (yards != null) return `Pass Yds: ${yards}`;
    return position || "";
  }
  const homeruns = player.stats?.hitting?.career?.homeruns || "0";
  return `${catalog.featured?.statLabel || "HR"}: ${homeruns}`;
}

export function mergeDirectoryOptions({
  sport,
  players = [],
  teamNames = [],
} = {}) {
  const catalog = getPlayerSearchCatalog(sport);
  const playerOptions = players
    .filter((player) => player?.playerId)
    .map((player) => ({
      type: "player",
      id: player.playerId,
      label: playerLabel(player, sport),
      secondary: playerSecondary(player, sport, catalog),
      group: DIRECTORY_GROUPS.players,
    }));

  if (!catalog.teamsEnabled) return playerOptions;

  const teamOptions = teamNames.filter(Boolean).map((team) => {
    const id = typeof team === "string" ? team : team.id;
    const label = typeof team === "string" ? team : team.label || team.name;
    return {
      type: "team",
      id,
      label,
      secondary: "Franchise",
      group: DIRECTORY_GROUPS.teams,
    };
  });

  return [...playerOptions, ...teamOptions];
}

function featuredFromMlb(player, catalog) {
  if (!player?.playerId) return null;
  const career = player.stats?.hitting?.career;
  return {
    playerId: player.playerId,
    name: formatPlayerName(player),
    subtitle: career?.team || "",
    statLabel: catalog.featured.statLabel,
    statValue: career?.homeruns ?? player.homerunsNumeric ?? "–",
    sport: "mlb",
  };
}

function featuredFromNba(player, catalog) {
  if (!player?.playerId) return null;
  return {
    playerId: player.playerId,
    name: getNbaDisplayName(player),
    subtitle:
      player.position || player.bio?.position || player.bioData?.position || "",
    statLabel: catalog.featured.statLabel,
    statValue: player.points ?? player.stats?.totals?.career?.pts ?? "–",
    sport: "nba",
  };
}

function featuredFromNfl(player, catalog) {
  if (!player?.playerId) return null;
  return {
    playerId: player.playerId,
    name: player.name || formatPlayerName(player),
    subtitle: player.position || player.bio?.position || player.bioData?.position || "",
    statLabel: catalog.featured.statLabel,
    statValue: player.passingYards ?? player.stats?.passing?.career?.yds ?? "–",
    sport: "nfl",
  };
}

export function toFeaturedPlayerTiles(sport, rows = []) {
  const catalog = getPlayerSearchCatalog(sport);
  if (!catalog.featured) return [];
  const mapRow =
    sport === "nba"
      ? featuredFromNba
      : sport === "nfl"
        ? featuredFromNfl
        : featuredFromMlb;
  return rows.map((row) => mapRow(row, catalog)).filter(Boolean);
}
